// src/components/clients/ClientCreationModal.jsx
import React, { useState } from 'react';
import { Modal, Form, Input, Select, Button, message, Spin, Row, Col, Divider } from 'antd';
import { SearchOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { createClient, searchClientByCuit } from '../../services/clientService';
import { validateCuitAFIP } from '../../services/cuitService';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { Option } = Select;

const ClientCreationModal = ({ visible, onClose, onClientCreated }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [validatingCuit, setValidatingCuit] = useState(false);
    const [cuitValidated, setCuitValidated] = useState(false);
    const [afipData, setAfipData] = useState(null);

    // Handle CUIT validation with AFIP
    const handleValidateCuit = async () => {
        const cuit = form.getFieldValue('documento_nro');
        
        if (!cuit || cuit.trim().length === 0) {
            message.warning('Por favor ingrese un CUIT');
            return;
        }

        // Clean CUIT (remove hyphens and spaces)
        const cleanCuit = cuit.replace(/[-\s]/g, '');
        
        if (cleanCuit.length < 10 || cleanCuit.length > 11) {
            message.error('CUIT debe tener 10-11 dígitos');
            return;
        }

        setValidatingCuit(true);
        setCuitValidated(false);
        setAfipData(null);

        try {
            // First check if client already exists in local DB (404 is expected for new clients)
            let existingClient = null;
            try {
                existingClient = await searchClientByCuit(cleanCuit);
            } catch (err) {
                // Ignore errors from local search - client doesn't exist locally, which is fine
                console.log('Client not found locally (expected for new clients)');
            }
            
            if (existingClient) {
                message.warning(`Cliente ya existe en la base de datos: ${existingClient.razon_social}`);
                setValidatingCuit(false);
                return;
            }

            // Validate with AFIP using the same service as invoice preview
            const afipData = await validateCuitAFIP(cleanCuit);
            
            if (afipData && afipData.razon_social) {
                message.success('CUIT válido en AFIP - Cliente encontrado');
                setCuitValidated(true);
                setAfipData(afipData);
                
                // Pre-fill form with AFIP data
                form.setFieldsValue({
                    documento_nro: afipData.documento_nro || cleanCuit,
                    razon_social: afipData.razon_social,
                    condicion_impositiva: afipData.condicion_impositiva,
                    direccion: afipData.direccion || '',
                    localidad: afipData.localidad || '',
                    provincia: afipData.provincia || '',
                    codigopostal: afipData.codigopostal || ''
                });
            } else {
                message.error('CUIT no encontrado en AFIP');
                setCuitValidated(false);
            }
        } catch (error) {
            console.error('Error validating CUIT with AFIP:', error);
            
            // Check if it's an AFIP validation error
            if (error.response && error.response.data) {
                message.error(error.response.data.message || 'Error al validar CUIT con AFIP');
            } else {
                message.error('Error al conectar con el servicio de validación AFIP');
            }
            setCuitValidated(false);
        } finally {
            setValidatingCuit(false);
        }
    };

    // Handle form submission
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            if (!cuitValidated) {
                message.warning('Por favor valide el CUIT con AFIP primero');
                return;
            }

            setLoading(true);

            // Create client
            const response = await createClient({
                documento_nro: values.documento_nro.replace(/[-\s]/g, ''),
                documento_tipo: 'CUIT',
                razon_social: values.razon_social,
                condicion_impositiva: values.condicion_impositiva,
                direccion: values.direccion || '',
                localidad: values.localidad || '',
                provincia: values.provincia || '',
                codigopostal: values.codigopostal || '',
                email: values.email || '',
                telefono: values.telefono || '',
                estado: 'ACTIVO'
            });

            if (response.success) {
                message.success('Cliente creado exitosamente');
                form.resetFields();
                setCuitValidated(false);
                setAfipData(null);
                onClientCreated(response.client);
                onClose();
            }
        } catch (error) {
            console.error('Error creating client:', error);
            
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                if (errorData.error === 'Client already exists') {
                    message.error(`Cliente ya existe: ${errorData.existingClient?.razon_social || ''}`);
                } else {
                    message.error(errorData.message || 'Error al crear cliente');
                }
            } else {
                message.error('Error al crear cliente');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle modal close
    const handleCancel = () => {
        form.resetFields();
        setCuitValidated(false);
        setAfipData(null);
        onClose();
    };

    return (
        <Modal
            title="Crear Nuevo Cliente"
            open={visible}
            onCancel={handleCancel}
            width={700}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancelar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={isDemoMode || !cuitValidated}
                >
                    Crear Cliente
                </Button>
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                autoComplete="off"
            >
                <Divider orientation="left">Validación CUIT</Divider>
                
                <Row gutter={16}>
                    <Col span={16}>
                        <Form.Item
                            name="documento_nro"
                            label="CUIT"
                            rules={[
                                { required: true, message: 'Por favor ingrese el CUIT' },
                                { pattern: /^[0-9-]{10,13}$/, message: 'CUIT inválido' }
                            ]}
                        >
                            <Input
                                placeholder="20-12345678-9"
                                maxLength={13}
                                disabled={cuitValidated}
                                suffix={
                                    cuitValidated ? (
                                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                    ) : (
                                        <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
                                    )
                                }
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label=" ">
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleValidateCuit}
                                loading={validatingCuit}
                                disabled={cuitValidated}
                                block
                            >
                                Validar AFIP
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>

                {cuitValidated && (
                    <>
                        <Divider orientation="left">Información del Cliente</Divider>

                        <Form.Item
                            name="razon_social"
                            label="Razón Social"
                            rules={[{ required: true, message: 'Por favor ingrese la razón social' }]}
                        >
                            <Input placeholder="Empresa SA" />
                        </Form.Item>

                        <Form.Item
                            name="condicion_impositiva"
                            label="Condición Impositiva"
                            rules={[{ required: true, message: 'Por favor seleccione la condición impositiva' }]}
                        >
                            <Select placeholder="Seleccione condición impositiva">
                                <Option value="RESPONSABLE INSCRIPTO">Responsable Inscripto</Option>
                                <Option value="MONOTRIBUTO">Monotributo</Option>
                                <Option value="EXENTO">Exento</Option>
                                <Option value="CONSUMIDOR FINAL">Consumidor Final</Option>
                            </Select>
                        </Form.Item>

                        <Divider orientation="left">Datos de Contacto</Divider>

                        <Form.Item
                            name="direccion"
                            label="Dirección"
                        >
                            <Input placeholder="Av. Corrientes 1234" />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="localidad"
                                    label="Localidad"
                                >
                                    <Input placeholder="CABA" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="provincia"
                                    label="Provincia"
                                >
                                    <Input placeholder="Buenos Aires" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="codigopostal"
                                    label="Código Postal"
                                >
                                    <Input placeholder="1043" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="telefono"
                                    label="Teléfono"
                                >
                                    <Input placeholder="011-1234-5678" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[{ type: 'email', message: 'Email inválido' }]}
                                >
                                    <Input placeholder="email@ejemplo.com" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                {validatingCuit && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin tip="Validando CUIT con AFIP..." />
                    </div>
                )}

                {!cuitValidated && !validatingCuit && (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                        Ingrese un CUIT y haga clic en "Validar AFIP" para continuar
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default ClientCreationModal;
